import AddTermModal from './AddTermModal';
import AdjustTermQuotaModal from './AdjustTermQuotaModal';
import AllocationDialog from '@components/accounts/AllocationDialog';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DataGrid from '@components/common/DataGrid';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DeleteTermDialog from './DeleteTermDialog';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExtendTermDatesModal from './ExtendTermDatesModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import QuotaUnitLabel from '@components/common/quota/QuotaUnitLabel';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StatusCell from '@components/common/StatusCell';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {deleteProductAccessTerm} from '@account/api';
import {deriveExpiredTermLabel} from '@account/utils/termLedgerLabel';
import {displayDate, formatPATDuration} from '@account/utils/dates';
import {getActivationStateColor} from '@account/utils/styles';
import {renderQuota, renderQuotaAmount} from '@account/utils/quota';
import {useFlags} from '@hooks/useFlags';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useProductAccessLedgerEntries} from '@hooks/data/products/useProductAccessLedgerEntries';
import {useProductAccessTerms} from '@hooks/data/products/useProductAccessTerms';

const HIGHLIGHT_DURATION_MS = 2500;

// CGO-3253: extending a CA-level term does not extend the workspace terms funded
// by it, so the CA reads active while every workspace under it stays expired and
// its allocated quota sits stranded in a dead window. Disabled until the cascade
// lands. Deliberately a constant rather than a LaunchDarkly flag: the only thing
// a flag adds is turning this back on without a deploy, and the one situation
// where someone reaches for that is a live incident, which is exactly when we
// don't want it back. Flip to true to re-enable.
const TERM_EXTEND_ENABLED = false;

const TERM_EXTEND_DISABLED_REASON =
  'Temporarily unavailable (CGO-3253). Extending a term here does not extend ' +
  'the workspace terms funded by it. Use "Add access period" to create the ' +
  'window you need, then allocate it to the workspaces that need it. The new ' +
  'term starts as Pending, so switch the status filter to see it. This does ' +
  'not repair CAs extended before now - check each workspace term end date.';

const TERM_EXTEND_DISABLED_CAPTION =
  'Disabled (CGO-3253), use Add access period';

const ProductAccessTermsSection = ({
  productAccessId,
  productAccess,
  isPlanetAdmin,
  // Injectable so the enabled path stays covered while the constant is false.
  termExtendEnabled = TERM_EXTEND_ENABLED,
}) => {
  const queryClient = useQueryClient();
  const gridRef = useRef(null);
  const highlightTimerRef = useRef(null);

  const [highlightedPatId, setHighlightedPatId] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [isAddTermOpen, setIsAddTermOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [isExtendOpen, setIsExtendOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [kebabAnchorEl, setKebabAnchorEl] = useState(null);
  const [kebabTerm, setKebabTerm] = useState(null);
  const [allocateTerm, setAllocateTerm] = useState(null);

  const {showAllocationUI, showLedgerHistory} = useFlags();

  const customerAccountId = productAccess?.customer_account_id;
  const isTileBased = productAccess?.quota_units === 'tile';
  // Edit affordances (add/adjust/extend/delete access periods) are gated to the
  // same targeted audience as the allocation UI so CSMs don't see them.
  const showSectionActions =
    isPlanetAdmin && !!customerAccountId && !isTileBased && showAllocationUI;

  const {data: termsData, isLoading: termsLoading} = useProductAccessTerms(
    productAccessId,
    {enabled: isPlanetAdmin, keepPreviousData: true}
  );
  const terms = termsData?.results ?? termsData ?? [];

  // Grouped ledger feed (one row per operation, all windows) used to explain
  // terms the backend flags "expired" purely because end_date < now. Allocating
  // "from today forward" splits one entitlement window into a past-dated
  // fragment (full quota) plus the allocated window (drained to 0); the fragment
  // reads as a bare "Expired" chip even though nothing lapsed. retry:false so a
  // 404 in envs without the endpoint degrades to the plain "Expired" label.
  const {data: ledgerData} = useProductAccessLedgerEntries(
    productAccessId,
    {limit: 500, offset: 0},
    {
      enabled: !!productAccessId && isPlanetAdmin && showLedgerHistory,
      keepPreviousData: true,
      retry: false,
    }
  );
  const ledgerOps = ledgerData?.results ?? [];

  const activeTerm = terms.find(t => t.activation_state === 'active');

  const filteredTerms = statusFilter
    ? terms.filter(t => {
        return t.activation_state === statusFilter;
      })
    : terms;

  // Annotate each expired row with its operation-derived label (allocated /
  // recalled / holding slice) so the Status cell can show what actually happened
  // instead of a bare "Expired". null => genuine expiry, keep the plain chip.
  const rows = filteredTerms.map(t => ({
    ...t,
    derivedExpiredLabel:
      t.activation_state === 'expired'
        ? deriveExpiredTermLabel(t, ledgerOps)
        : null,
  }));

  const handleHighlightTerm = useCallback(patId => {
    clearTimeout(highlightTimerRef.current);
    setHighlightedPatId(patId);
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedPatId(null);
    }, HIGHLIGHT_DURATION_MS);
  }, []);

  const {mutate: deleteTerm, isLoading: isDeleting} = useMutation(
    ({supportTicketRef}) =>
      deleteProductAccessTerm(productAccessId, selectedTerm.id, {
        supportTicketRef,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccessTerms', productAccessId]);
        queryClient.invalidateQueries(['productAccess', productAccessId]);
        setIsDeleteOpen(false);
        setSelectedTerm(null);
      },
      onError: err => {
        setDeleteError(
          err?.response?.data?.message ?? 'Failed to delete term.'
        );
        setIsDeleteOpen(false);
      },
    }
  );

  const columns = useMemo(
    () => [
      {
        field: 'activation_state',
        headerName: 'Status',
        renderCell: params => {
          const state = params.value;
          const isActive = state === 'active';
          const derived = params.row.derivedExpiredLabel;
          // Expired fragment created by an allocation/recall/holding split:
          // show what actually happened instead of a bare "Expired" chip.
          if (state === 'expired' && derived) {
            return (
              <Stack spacing={0.25}>
                <StatusCell
                  status={derived.status}
                  tooltipText={derived.tooltip}
                  sx={theme => ({
                    color: theme.palette.text.secondary,
                    fontWeight: 'inherit',
                  })}
                />
                {derived.detail && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {derived.detail}
                  </Typography>
                )}
              </Stack>
            );
          }
          return (
            <Stack spacing={0.5}>
              <StatusCell
                status={state}
                sx={theme => ({
                  color: getActivationStateColor(theme, state),
                  fontWeight: 'inherit',
                })}
              />
              {isActive && (
                <Chip
                  label="Current"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{height: 16, fontSize: 10, width: 'fit-content'}}
                />
              )}
            </Stack>
          );
        },
        flex: 0.16,
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 0.16,
        type: 'date',
        valueGetter: params => (params.value ? new Date(params.value) : null),
        renderCell: params => (
          <Typography variant="body2">
            {displayDate(params.row.start_date)}
          </Typography>
        ),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 0.16,
        type: 'date',
        valueGetter: params => (params.value ? new Date(params.value) : null),
        renderCell: params => (
          <Stack spacing={0}>
            <Typography variant="body2">
              {displayDate(params.row.end_date)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatPATDuration(params.row.start_date, params.row.end_date)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'quota_limit',
        headerName: 'Unallocated Quota',
        flex: 0.2,
        valueGetter: params =>
          params.row.has_unlimited_quota
            ? 'Unlimited'
            : renderQuota(params.row.quota_limit, params.row.quota_units),
        renderCell: params =>
          params.row.has_unlimited_quota ? (
            <Typography variant="body2">Unlimited</Typography>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2">
                {renderQuotaAmount(
                  params.row.quota_limit,
                  params.row.quota_units
                )}
              </Typography>
              <QuotaUnitLabel unit={params.row.quota_units} variant="body2" />
            </Stack>
          ),
      },
      ...(isPlanetAdmin && showAllocationUI
        ? [
            {
              field: 'actions',
              headerName: '',
              sortable: false,
              renderCell: params => (
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setKebabAnchorEl(e.currentTarget);
                    setKebabTerm(params.row);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              ),
              flex: 0.14,
            },
          ]
        : []),
    ],
    [isPlanetAdmin, showAllocationUI]
  );

  return (
    <Stack sx={{gap: 1, marginTop: 5}}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Access Periods
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each row is a time window during which this product is active, along
            with its quota allocation.
          </Typography>
        </Box>

        {showSectionActions && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Creates a new time window during which this product is active, with its own quota allocation.">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsAddTermOpen(true)}
                endIcon={<InfoOutlinedIcon fontSize="small" color="disabled" />}
              >
                Add access period
              </Button>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{mb: 1}}>
        {[
          {label: 'All', value: null},
          {label: 'Active', value: 'active'},
          {label: 'Pending', value: 'pending'},
          {label: 'Expired', value: 'expired'},
        ].map(f => (
          <Chip
            key={f.label}
            label={f.label}
            size="small"
            onClick={() => setStatusFilter(f.value)}
            color={statusFilter === f.value ? 'primary' : 'default'}
            variant={statusFilter === f.value ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 120,
          '& .pat-highlight': {
            animation: 'patHighlight 2.5s ease-out',
          },
          '@keyframes patHighlight': {
            '0%': {backgroundColor: '#fff9c4'},
            '100%': {backgroundColor: 'transparent'},
          },
          '& .pat-active-row': {
            borderLeft: '3px solid',
            borderColor: 'success.main',
          },
        }}
        ref={gridRef}
      >
        <DataGrid
          autoHeight
          columns={columns}
          disableColumnFilter
          disableColumnSorting
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row': {cursor: 'default'},
            '& .MuiDataGrid-row:hover': {bgcolor: 'transparent'},
          }}
          getRowId={row => row.id}
          loading={termsLoading}
          rowHeight={64}
          rows={rows}
          getRowClassName={params => {
            if (params.id === highlightedPatId) return 'pat-highlight';
            if (activeTerm && params.id === activeTerm.id)
              return 'pat-active-row';
            return '';
          }}
          slots={{
            noRowsOverlay: () => (
              <Typography color="text.secondary" variant="body2" sx={{p: 2}}>
                No access periods found.
              </Typography>
            ),
          }}
        />
      </Box>

      {deleteError && (
        <Notifier
          open={!!deleteError}
          message={deleteError}
          severity="error"
          autoHideDuration={6000}
          onClose={() => setDeleteError(null)}
        />
      )}

      {isAddTermOpen && (
        <AddTermModal
          open={isAddTermOpen}
          onClose={() => setIsAddTermOpen(false)}
          productAccessId={productAccessId}
          productAccess={productAccess}
          existingTerms={terms}
          onHighlightTerm={handleHighlightTerm}
        />
      )}

      <Menu
        anchorEl={kebabAnchorEl}
        open={Boolean(kebabAnchorEl)}
        onClose={() => setKebabAnchorEl(null)}
        onClick={e => e.stopPropagation()}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
      >
        {['active', 'pending'].includes(kebabTerm?.activation_state) && (
          <MenuItem
            onClick={() => {
              setKebabAnchorEl(null);
              setAllocateTerm(kebabTerm);
            }}
          >
            <SendOutlinedIcon fontSize="small" sx={{mr: 1}} />
            Allocate to workspace
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setKebabAnchorEl(null);
            setSelectedTerm(kebabTerm);
            setIsAdjustOpen(true);
          }}
        >
          <EditOutlinedIcon fontSize="small" sx={{mr: 1}} />
          Adjust Quota
        </MenuItem>
        {termExtendEnabled ? (
          <MenuItem
            onClick={() => {
              setKebabAnchorEl(null);
              setSelectedTerm(kebabTerm);
              setIsExtendOpen(true);
            }}
          >
            <CalendarMonthOutlinedIcon fontSize="small" sx={{mr: 1}} />
            Extend Dates
          </MenuItem>
        ) : (
          // The disabled item's whole point is to route the admin to "Add
          // access period", which only renders under showSectionActions. On a
          // tile-based PA or one with no customer account that button is absent,
          // so the item would name a control that does not exist.
          showSectionActions && (
            <Tooltip title={TERM_EXTEND_DISABLED_REASON}>
              <span>
                <MenuItem disabled sx={{alignItems: 'flex-start'}}>
                  <CalendarMonthOutlinedIcon
                    fontSize="small"
                    sx={{mr: 1, mt: 0.25}}
                  />
                  <Stack>
                    <Typography variant="inherit">Extend Dates</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {TERM_EXTEND_DISABLED_CAPTION}
                    </Typography>
                  </Stack>
                </MenuItem>
              </span>
            </Tooltip>
          )
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setKebabAnchorEl(null);
            setSelectedTerm(kebabTerm);
            setIsDeleteOpen(true);
          }}
          sx={{color: 'error.main'}}
        >
          <DeleteIcon fontSize="small" sx={{mr: 1}} />
          Delete access period
        </MenuItem>
      </Menu>

      {isAdjustOpen && selectedTerm && (
        <AdjustTermQuotaModal
          open={isAdjustOpen}
          onClose={() => {
            setIsAdjustOpen(false);
            setSelectedTerm(null);
          }}
          productAccessId={productAccessId}
          term={selectedTerm}
        />
      )}

      {isExtendOpen && selectedTerm && (
        <ExtendTermDatesModal
          open={isExtendOpen}
          onClose={() => {
            setIsExtendOpen(false);
            setSelectedTerm(null);
          }}
          productAccessId={productAccessId}
          term={selectedTerm}
          onHighlightTerm={handleHighlightTerm}
        />
      )}

      {isDeleteOpen && selectedTerm && (
        <DeleteTermDialog
          term={selectedTerm}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedTerm(null);
          }}
          onConfirm={supportTicketRef => deleteTerm({supportTicketRef})}
          isLoading={isDeleting}
        />
      )}

      {allocateTerm && customerAccountId && (
        <AllocationDialog
          open={!!allocateTerm}
          onClose={() => setAllocateTerm(null)}
          accountId={String(customerAccountId)}
          productAccess={productAccess}
          term={allocateTerm}
        />
      )}
    </Stack>
  );
};

ProductAccessTermsSection.propTypes = {
  isPlanetAdmin: PropTypes.bool,
  termExtendEnabled: PropTypes.bool,
  productAccess: PropTypes.shape({
    customer_account_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    edp_id: PropTypes.string,
    quota_units: PropTypes.string,
    unlimited_quota: PropTypes.bool,
  }),
  productAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default ProductAccessTermsSection;
